// BHP-1160: renders a PDF that sits behind Bearer auth.
//
// The document cannot be handed to the browser as a URL: `<iframe src>` is a
// subresource load and carries no Authorization header, so the endpoint would
// answer 401 and the frame would show the browser's own error page. It is
// fetched as a Blob instead and turned into an object URL, which the iframe can
// load because it never leaves the document.
//
// That object URL is a reference the browser keeps alive until it is revoked;
// leaving it behind on every invoice a customer opens leaks the whole document
// for the lifetime of the tab. Hence the effect below, and hence the download
// link reusing the same URL instead of fetching the file a second time.
//
// There is no pdf.js here on purpose (PO decision, 2026-10-08): the browser's
// own viewer costs nothing and renders these documents fine. The trade is
// mobile — iOS Safari in particular refuses to display a PDF in a frame — which
// is why the download button is always present and never behind the viewer.
import { useEffect, useState } from 'react';

interface PdfViewerProps {
  document: Blob;
  /** Filename offered when the reader saves it. */
  fileName: string;
  /** Accessible name of the frame, e.g. "Invoice of 1 October 2026". */
  title: string;
}

export default function PdfViewer({
  document,
  fileName,
  title
}: PdfViewerProps): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const objectUrl = URL.createObjectURL(document);
    setUrl(objectUrl);

    // Revoke only. Clearing the state here would be a state update on a
    // component that is either unmounting (pointless, and React says so) or
    // about to be handed a new document, which sets its own URL on the next
    // line of work — blanking it in between would flash the loading text.
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [document]);

  if (url === null) {
    return <p className="text-sm text-slate-500">Opening document…</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <a
          href={url}
          download={fileName}
          className="rounded-md border border-solid border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 no-underline hover:bg-slate-50"
        >
          Download PDF
        </a>
      </div>
      {/*
        No `sandbox` here, and that is a measured decision rather than an
        oversight. It carried `sandbox="allow-same-origin"` for exactly one
        afternoon: Chrome's PDF viewer is itself scripted, so a frame without
        `allow-scripts` cannot run it and shows a grey panel with a broken-file
        icon instead of the invoice. Adding `allow-scripts` back next to
        `allow-same-origin` is no protection at all — that pair lets a frame
        remove its own sandbox, which the HTML spec says in so many words.

        What actually closes the hole is one layer up: httpClient.getFile()
        refuses a body whose type is not the one it asked for, so a blob of
        text/html — the thing that would otherwise render as same-origin markup
        with the portal's session behind it — never reaches this component.
      */}
      <iframe
        src={url}
        title={title}
        className="h-[70vh] min-h-96 w-full rounded-lg border border-solid border-slate-200 bg-white"
      />
      <p className="text-xs text-slate-400">
        Does the document stay blank? Use the download button — not every
        browser can display a PDF in the page.
      </p>
    </div>
  );
}
